import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Dialog, SpinLoading, Toast } from 'antd-mobile';
import toyotaLogo from '../../assets/TOYOTA LOGO - Horizontal.svg';
import carServiceBookingSvg from '../../assets/car-service-booking.svg';
import useLineLiff from '../../hooks/LineLiff';
import { fetchBookedDetail, fetchBpBookedDetail, cancelBooking, fetchInsuranceClaimUrl } from './CarServiceBookingAction';

import './common.scss';
import './CarServiceBookingRecordDetail.scss';

function buildICS({ dateTime, title, location, description }) {
  const [date, time] = (dateTime || '').split(' ');
  const datePart = (date || '').replace(/\//g, '');
  const timePart = (time || '09:00').replace(':', '');
  const startDt = `${datePart}T${timePart}00`;
  const startH = parseInt(timePart.slice(0, 2), 10);
  const endH = String(Math.min(startH + 2, 23)).padStart(2, '0');
  const endDt = `${datePart}T${endH}${timePart.slice(2)}00`;

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Toyota//Toyota LINE Webview//ZH',
    'BEGIN:VEVENT',
    `DTSTART:${startDt}`,
    `DTEND:${endDt}`,
    `SUMMARY:${title}`,
    `LOCATION:${location}`,
    `DESCRIPTION:${description}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}

export default function CarServiceBookingRecordDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { liff } = useLineLiff(process.env.REACT_APP_LIFF_BOOKING);
  const initialRecord = useMemo(() => location.state || {}, [location.state]);

  const isMaintenance = initialRecord.type === 'maintenance';
  const carNo = searchParams.get('carNo') || initialRecord.plate;
  const sKey = searchParams.get('sKey');

  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(initialRecord);
  const [claimWarningVisible, setClaimWarningVisible] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const loadDetail = async () => {
      await liff.ready;

      if (isMaintenance) {
        const { dlrCode, brnhCode, sKey: recordSKey, workNo, plate } = initialRecord;
        if (!dlrCode || !brnhCode || !recordSKey || !workNo) {
          setLoading(false);
          return;
        }
        try {
          const idToken = liff.getIDToken();
          const response = await fetchBookedDetail(
            idToken, plate || carNo, dlrCode, brnhCode, recordSKey, workNo
          );
          if (response.success && response.data) {
            const detail = response.data;
            setRecord({
              ...initialRecord,
              dateTime: detail.book_date && detail.book_time
                ? `${detail.book_date} ${detail.book_time}`
                : initialRecord.dateTime,
              plate: detail.car_no || initialRecord.plate,
              centerFull:
                `${detail.dlr_name || ''} ${detail.brnh_name || ''}`.trim() ||
                initialRecord.centerFull,
              center: detail.brnh_name || initialRecord.center,
              centerPhone: detail.brnh_tel || '',
              serviceItem: detail.service_content || initialRecord.serviceItem,
              staff: detail.service_advisor || '',
              wait: detail.gv_type === 'Y' ? '是' : detail.gv_type === 'N' ? '否' : '',
              repairs: detail.remark || '',
              contactName: detail.contact_name || '',
              contactTel: detail.contact_tel || '',
              status: detail.status || '',
              canChangeFactory: detail.can_change_factory || '',
              address: detail.address || '',
            });
          }
        } catch (error) {
          console.error('Failed to fetch booking detail:', error);
        } finally {
          setLoading(false);
        }
      } else {
        const { dlrCode, brnhCode, bpEvaNo, plate } = initialRecord;
        if (!dlrCode || !brnhCode || !bpEvaNo) {
          setLoading(false);
          return;
        }
        try {
          const idToken = liff.getIDToken();
          const response = await fetchBpBookedDetail(
            idToken, plate || carNo, dlrCode, brnhCode, bpEvaNo
          );
          if (response.success && response.data) {
            const detail = response.data;
            setRecord({
              ...initialRecord,
              dateTime: detail.book_date && detail.time_set
                ? `${detail.book_date} ${detail.time_set}`
                : initialRecord.dateTime,
              plate: detail.car_no || initialRecord.plate,
              centerFull:
                `${detail.dlr_name || ''} ${detail.brnh_name || ''}`.trim() ||
                initialRecord.centerFull,
              center: detail.brnh_name || initialRecord.center,
              centerPhone: detail.brnh_tel || '',
              address: detail.brnh_addr || '',
              serviceItem: initialRecord.serviceItem,
              name: detail.contact_name || '',
              phone: detail.contact_tel || '',
              payMethod: (detail.insur === 'Y' || detail.insur === '保險') ? '保險' : '自費',
              insuranceCompany: detail.insur_cd || '',
              insuranceData: detail.insur_no || '',
              helpType: detail.help_type === 'A' ? '自行出險' : detail.help_type === 'B' ? '入廠辦理' : '',
            });
          }
        } catch (error) {
          console.error('Failed to fetch bp booking detail:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadDetail();
  }, [initialRecord, carNo]);

  const handleAddToCalendar = () => {
    const icsContent = buildICS({
      dateTime: record.dateTime,
      title: isMaintenance ? 'Toyota 保養維修預約' : 'Toyota 板噴估價預約',
      location: record.centerFull || record.center || '',
      description: isMaintenance
        ? `車牌: ${record.plate}\\n服務項目: ${record.serviceItem}\\n維修需求: ${record.repairs || ''}`
        : `車牌: ${record.plate || carNo}\\n服務項目: ${record.serviceItem}`,
    });
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = isMaintenance ? 'toyota-booking.ics' : 'toyota-paint-booking.ics';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCancel = () => {
    Dialog.confirm({
      content: `您確定要取消 ${record.dateTime || ''} 的預約保養嗎？`,
      confirmText: '確認取消',
      cancelText: '我再想想',
      onConfirm: async () => {
        try {
          const idToken = liff.getIDToken();
          const response = await cancelBooking(
            idToken, carNo, record.sKey, record.dlrCode, record.brnhCode, record.workNo
          );
          if (response.success) {
            await Dialog.alert({ content: '預約已成功取消', confirmText: '確定' });
            navigate(-1);
          } else {
            Dialog.alert({ content: response.message || '取消預約失敗，請稍後再試', confirmText: '確定' });
          }
        } catch {
          Dialog.alert({ content: '取消預約失敗，請稍後再試', confirmText: '確定' });
        }
      },
    });
  };

  const handleChange = () => {
    const params = new URLSearchParams();
    if (carNo) params.set('carNo', carNo);
    if (sKey) params.set('sKey', sKey);
    const queryString = params.toString();
    navigate(
      queryString
        ? `/hotai/toyota/car-service-booking?${queryString}`
        : '/hotai/toyota/car-service-booking',
      { state: { prefillRecord: record } }
    );
  };

  const handleGoToInsurance = async () => {
    setClaimWarningVisible(false);
    try {
      const urlResponse = await fetchInsuranceClaimUrl(
        liff.getIDToken(),
        record.dlrCode,
        record.brnhCode,
        record.bpEvaNo
      );
      if (urlResponse.success && urlResponse.data?.url) {
        window.open(urlResponse.data.url, '_blank');
      } else {
        Toast.show({ content: '無法取得出險平台連結', icon: 'fail' });
      }
    } catch (error) {
      console.error(error);
      Toast.show({ content: '無法取得出險平台連結', icon: 'fail' });
    }
    setSuccessVisible(true);
    setTimeout(() => setSuccessVisible(false), 3000);
  };

  return (
    <div className="center-viewport">
      <div className="maintenance-app">
        <div className="toyota-header">
          <img className="toyota-logo" src={toyotaLogo} alt="TOYOTA LOGO" />
        </div>

        <div className="main-content">
          <div className="content-header">
            <div className="header-row">
              <div className="header-text">
                <div className="vehicle-number">{record.plate || carNo || ''}</div>
                <h1 className="page-title">
                  {isMaintenance ? '保養維修預約紀錄' : '板噴估價預約紀錄'}
                </h1>
              </div>
              <div className="header-icons">
                <img src={carServiceBookingSvg} alt="預約紀錄icon" />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="bkrd-loading">
              <SpinLoading color="primary" />
            </div>
          ) : (
            <>
              <div className="bkrd-time-header">
                <span className="bkrd-time-header-label">預約時間</span>
                <span className="bkrd-time-header-value">{record.dateTime}</span>
              </div>

              <div className="bkrd-detail-card">
                <div className="bkrd-detail-rows">
                  <div className="bkrd-detail-row">
                    <span className="bkrd-detail-label">車牌</span>
                    <span className="bkrd-detail-value">{record.plate || carNo || ''}</span>
                  </div>
                  <div className="bkrd-detail-row">
                    <span className="bkrd-detail-label">服務據點</span>
                    <span className="bkrd-detail-value">{record.centerFull || record.center}</span>
                  </div>
                  {record.centerPhone && (
                    <div className="bkrd-detail-row">
                      <span className="bkrd-detail-label">據點電話</span>
                      <span className="bkrd-detail-value">{record.centerPhone}</span>
                    </div>
                  )}
                  <div className="bkrd-detail-row">
                    <span className="bkrd-detail-label">服務項目</span>
                    <span className="bkrd-detail-value">{record.serviceItem}</span>
                  </div>

                  {isMaintenance ? (
                    <>
                      {record.staff && (
                        <div className="bkrd-detail-row">
                          <span className="bkrd-detail-label">上次接待專員</span>
                          <span className="bkrd-detail-value">{record.staff}</span>
                        </div>
                      )}
                      {record.wait && (
                        <div className="bkrd-detail-row">
                          <span className="bkrd-detail-label">在廠等待</span>
                          <span className="bkrd-detail-value">{record.wait}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {record.payMethod && (
                        <div className="bkrd-detail-row">
                          <span className="bkrd-detail-label">付費方式</span>
                          <span className="bkrd-detail-value">{record.payMethod}</span>
                        </div>
                      )}
                      {record.insuranceCompany && (
                        <div className="bkrd-detail-row">
                          <span className="bkrd-detail-label">產險公司</span>
                          <span className="bkrd-detail-value">{record.insuranceCompany}</span>
                        </div>
                      )}
                      {record.insuranceData && (
                        <div className="bkrd-detail-row">
                          <span className="bkrd-detail-label">保險資料</span>
                          <span className="bkrd-detail-value">{record.insuranceData}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {isMaintenance && record.repairs && (
                  <>
                    <div className="bkrd-divider" />
                    <div className="bkrd-detail-row">
                      <span className="bkrd-detail-label">維修需求</span>
                      <span className="bkrd-detail-value">{record.repairs}</span>
                    </div>
                  </>
                )}

                {!isMaintenance && (record.name || record.phone) && (
                  <>
                    <div className="bkrd-divider" />
                    <div className="bkrd-detail-rows bkrd-detail-rows--no-pb">
                      {record.name && (
                        <div className="bkrd-detail-row">
                          <span className="bkrd-detail-label">聯絡人</span>
                          <span className="bkrd-detail-value">{record.name}</span>
                        </div>
                      )}
                      {record.phone && (
                        <div className="bkrd-detail-row">
                          <span className="bkrd-detail-label">聯絡電話</span>
                          <span className="bkrd-detail-value">{record.phone}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div className="bkrd-divider" />

                <button type="button" className="bkrd-action-btn" onClick={handleAddToCalendar}>
                  加入行事曆
                </button>

                {isMaintenance ? (
                  <>
                    <button type="button" className="bkrd-action-btn" onClick={handleCancel}>
                      取消預約
                    </button>
                    <button type="button" className="bkrd-action-btn" onClick={handleChange}>
                      更改預約
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="bkrd-action-btn"
                    disabled={!(record.payMethod === '保險' && record.helpType === '自行出險')}
                    onClick={() => setClaimWarningVisible(true)}
                  >
                    填寫出險資料
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {!isMaintenance && (
        <>
          <Dialog
            visible={claimWarningVisible}
            showCloseButton={false}
            content={
              <div className="paint-dialog">
                <div className="paint-dialog-title">前往出險平台</div>
                <div className="paint-claim-text">
                  <p>
                    預約成功！您即將離開本頁面，前往保險公司出險申請平台辦理相關理賠程序。出險平台之服務內容與個人資料保護規定，將依該平台規範辦理。
                  </p>
                  <p>
                    請確認您已備妥相關資料(如行照、駕照、事故三聯單等)，再繼續操作，是否前往出險平台？
                  </p>
                  <p>
                    若稍後再填寫可先按取消，之後再到首頁-預約及查詢前往出險平台。
                  </p>
                </div>
                <div className="paint-dialog-actions">
                  <button
                    type="button"
                    className="paint-dialog-btn paint-dialog-btn--cancel"
                    onClick={() => setClaimWarningVisible(false)}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    className="paint-dialog-btn paint-dialog-btn--ok"
                    onClick={handleGoToInsurance}
                  >
                    繼續前往
                  </button>
                </div>
              </div>
            }
            actions={[]}
          />

          {successVisible && (
            <div className="paint-success-overlay">
              <div className="paint-success-toast">
                <div className="paint-success-icon">
                  <div className="paint-success-ring paint-success-ring--outer" />
                  <div className="paint-success-ring paint-success-ring--mid" />
                  <div className="paint-success-ring paint-success-ring--inner">
                    <svg viewBox="0 0 24 24" fill="none" width="28" height="28">
                      <path
                        d="M5 13l4 4L19 7"
                        stroke="#fff"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
                <p className="paint-success-title">預約成功</p>
                <p className="paint-success-sub">3 秒後將回到 LINE</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
